import { Component, inject, Inject, Input, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { BitstreamDataService } from '../../../../core/data/bitstream-data.service';

import { Bitstream } from '../../../../core/shared/bitstream.model';
import { Item } from '../../../../core/shared/item.model';
import { RemoteData } from '../../../../core/data/remote-data';
import { hasValue } from '../../../../shared/empty.util';
import { PaginatedList } from '../../../../core/data/paginated-list.model';
import { NotificationsService } from '../../../../shared/notifications/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { getFirstCompletedRemoteData } from '../../../../core/shared/operators';
import { AppConfig, APP_CONFIG } from 'src/config/app-config.interface';
import { DSONameService } from '../../../../core/breadcrumbs/dso-name.service';
import { GalleryService } from 'src/app/shared/gallery/gallery.service';
import { FeatureID } from 'src/app/core/data/feature-authorization/feature-id';
import { AuthorizationDataService } from 'src/app/core/data/feature-authorization/authorization-data.service';

/**
 * This component renders the file section of the item
 * inside a 'ds-metadata-field-wrapper' component.
 */
@Component({
  selector: 'ds-item-page-preservation-file-section',
  templateUrl: './preservation-file-section.component.html'
})
export class PreservationFileSectionComponent implements OnInit {

  @Input() item: Item;

  label = 'item.page.protected.files';
  separator = '<br/>';

  bitstreams$: BehaviorSubject<Bitstream[]>;
  preservationBitstreams$: BehaviorSubject<Bitstream[]>;

  currentPage: number;

  isLoading: boolean;

  isLastPage: boolean;

  pageSize: number;

  private totalPages: number;

  private itemBitstreams: Bitstream[] = undefined;

  private galleryService: GalleryService = inject( GalleryService );
  private authorizationService: AuthorizationDataService = inject( AuthorizationDataService );

  constructor(
    protected bitstreamDataService: BitstreamDataService,
    protected notificationsService: NotificationsService,
    protected translateService: TranslateService,
    public dsoNameService: DSONameService,
    @Inject(APP_CONFIG) protected appConfig: AppConfig
  ) {
    this.pageSize = this.appConfig.item.bitstream.pageSize;
  }

  ngOnInit(): void {
    this.getNextPage();
  }

  /// Downloads the list of bitstreams that are available for this item
  private getBitstreams(): void {
    this.isLoading = true;
    this.bitstreamDataService.findAllByItemAndBundleName(
      this.item,
      'PRESERVATION',{}).pipe(
        getFirstCompletedRemoteData()
      ).subscribe((bitstreamsRD: RemoteData<PaginatedList<Bitstream>>) => {
        if (bitstreamsRD.errorMessage ) {
          this.notificationsService.error(this.translateService.get('file-section.error.header'), `${bitstreamsRD.statusCode} ${bitstreamsRD.errorMessage}`);  
        } else if (hasValue(bitstreamsRD.payload)) {
          this.itemBitstreams = bitstreamsRD.payload.page;

          for ( const bs of this.itemBitstreams ){
            this.galleryService.startLoading();
            this.authorizationService.isAuthorized( FeatureID.CanDownload, bs.self).subscribe( ( isAuthorized: boolean ) => {
                if ( isAuthorized ) {
                  this.galleryService.addItem( 'PRESERVATION', this.item.handle, bs );
                }
                this.galleryService.finishLoading();
              }
            );
          }

          /// Calculate the total number of pages as we use this in later calculations
          this.totalPages = Math.floor( this.itemBitstreams.length / this.pageSize );
          if ( this.itemBitstreams.length % this.pageSize !== 0 ){
            this.totalPages = this.totalPages + 1;
          }

          this.updateFileList();

          this.isLoading = false;
        }
      });
  }

  /// Updates the file list based on the current page and number of pages
  private updateFileList(): void {
    /// Get the new bitstreams we'd like to display from the bitstream array
    let newBitstreams: Bitstream[];
    let start: number = (this.currentPage - 1) * this.pageSize;
    let end: number = this.currentPage * this.pageSize;
    if (end >= this.itemBitstreams.length){
      newBitstreams = this.itemBitstreams.slice(start);
    } else {
      newBitstreams = this.itemBitstreams.slice(start, end);
    }

    /// Now add these bitstreams to the list that is displayed
    const current: Bitstream[] = this.bitstreams$.getValue();
    if (current.length > 0 ){
      this.bitstreams$.next([...current, ...newBitstreams]);
    } else {
      this.bitstreams$.next([...newBitstreams]);
    }

    /// Show the more or less button
    if (this.currentPage === this.totalPages){
      this.isLastPage = true;
    } else {
      this.isLastPage = false;
    }
  }

   ///Get the next page of bitstreams to display in the list
  getNextPage(): void {
    if (this.currentPage === undefined) {
      this.currentPage = 1;
      this.bitstreams$ = new BehaviorSubject([]);

      /// Only download files list if we haven't yet
      if (this.itemBitstreams === undefined) {
        this.getBitstreams();
      } else {
        this.updateFileList();
      }
    } else {
      this.currentPage++;
      this.updateFileList();
    }
  }
}
